"use client"

import { Check, Copy } from "lucide-react"
import { useState } from "react"

import { cn } from "@/lib/utils"

/*
 * Copy-pasteable asset: AI prompt, email/script template, checklist.
 * Monospace body with a copy-to-clipboard button. The reader steals this.
 */
export function CopyBlock({
  label,
  copyLabel,
  copiedLabel,
  code,
  className,
}: {
  label: string
  copyLabel: string
  copiedLabel: string
  code: string
  /** Overrides the article spacing (`my-8`) in denser layouts. */
  className?: string
}) {
  const [copied, setCopied] = useState(false)

  function onCopy() {
    void navigator.clipboard.writeText(code).then(() => {
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div
      className={cn(
        "not-prose bg-background ring-border/60 my-8 overflow-hidden rounded-xl shadow-sm ring-1",
        className,
      )}
    >
      <div className="border-border/60 flex items-center justify-between gap-3 border-b px-4 py-2.5">
        <span className="text-muted-foreground text-2xs font-mono tracking-wider uppercase">
          {label}
        </span>
        <button
          type="button"
          onClick={onCopy}
          aria-live="polite"
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
            copied
              ? "text-primary-cta"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {copied ? (
            <Check aria-hidden className="size-3.5" />
          ) : (
            <Copy aria-hidden className="size-3.5" />
          )}
          {copied ? copiedLabel : copyLabel}
        </button>
      </div>
      <pre className="text-foreground text-2sm overflow-x-auto px-4 py-4 leading-relaxed">
        <code className="font-mono whitespace-pre-wrap">{code}</code>
      </pre>
    </div>
  )
}
